import React, {useEffect, useRef, useState} from 'react';
import {View, Platform, StyleProp, ViewStyle} from 'react-native';
import CCText from '@/Atoms/CCText';
import {Popover, ArrowContainer} from 'react-tiny-popover';

const Tooltip = ({
  children,
  text,
  /** Rich popover content; when set, replaces the default `text` label. */
  body,
  position = 'top',
  /** Priority-ordered list of sides to try (react-tiny-popover flips to the
   * next entry when the previous one would overflow the boundary). Optional
   * -- when omitted, behaves exactly as before: a single fixed `[position]`,
   * which never flips (react-tiny-popover only repositions/flips across
   * multiple candidate positions; with just one it can only nudge within
   * that same side, which isn't enough room near a viewport edge). Existing
   * consumers that only pass `position` are unaffected. */
  positions,
  /** Minimum gap (px) the popover keeps from the boundary edge when
   * flipping/nudging to stay on-screen. Optional -- omitted means react-
   * tiny-popover's own default (0), same as before. */
  boundaryInset,
  backgroundColor,
  textcolor,
  borderColor,
  arrowColor,
  fontSize,
  width,
  height,
  paddingVertical,
  borderWidth,
  contentStyle,
  /** Fired on every internal isOpen transition -- toggle()-driven open/close,
   * onClickOutside, AND the 10s safety-close -- so a consumer that needs to
   * mirror real open/close state (rather than blindly calling toggle()) has
   * something to key off instead of drifting out of sync with it. Optional;
   * existing consumers that don't pass it are unaffected. */
  onOpenChange,
  /** 'default' (unset) keeps the original dark speech-bubble chrome exactly
   * as before -- fully byte-compatible for existing consumers. 'card' swaps
   * the *defaults* to a light elevated card (white bg, subtle border, soft
   * shadow, blended-in arrow) for consumers showing rich content like a
   * profile preview rather than a short text tip. Any of the explicit
   * color/border props above still win over either variant's defaults. */
  variant = 'default',
}: {
  children: any;
  text?: string;
  body?: React.ReactNode;
  position?: string;
  positions?: Array<'top' | 'bottom' | 'left' | 'right'>;
  boundaryInset?: number;
  backgroundColor?: string;
  textcolor?: string;
  borderColor?: string;
  arrowColor?: string;
  fontSize?: number;
  width?: number;
  height?: number;
  paddingVertical?: number;
  borderWidth?: number;
  contentStyle?: StyleProp<ViewStyle>;
  onOpenChange?: (isOpen: boolean) => void;
  variant?: 'default' | 'card';
}) => {
  const isCard = variant === 'card';
  const [isOpen, setIsOpen] = useState(false);
  const openTimestampRef = useRef<number | null>(null);

  function toggle() {
    setIsOpen(prev => {
      const nextState = !prev;
      if (nextState) {
        openTimestampRef.current = Date.now(); // mark open time
      } else {
        openTimestampRef.current = null;
      }
      return nextState;
    });
  }

  // Safety check effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isOpen) {
      timer = setTimeout(() => {
        if (openTimestampRef.current) {
          const elapsed = Date.now() - openTimestampRef.current;
          if (elapsed >= 10000) {
            setIsOpen(false); // force close if > 5s
            openTimestampRef.current = null;
          }
        }
      }, 10000);
    }
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Report every real isOpen transition, however it happened (toggle(),
  // onClickOutside below, or the safety-close above) -- all of them funnel
  // through setIsOpen, so watching `isOpen` itself is the one place that
  // can't miss a path.
  useEffect(() => {
    onOpenChange?.(isOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const popoverContainerStyle: ViewStyle = {
    backgroundColor: backgroundColor || (isCard ? '#FFFFFF' : '#000'),
    borderRadius: isCard ? 8 : 10,
    paddingVertical: paddingVertical ?? 12,
    paddingHorizontal: 16,
    maxWidth: width || 300,
    height: height || undefined,
    borderWidth: borderWidth ?? 1,
    borderColor: borderColor || (isCard ? '#22222220' : '#000'),
    borderStyle: 'solid',
    ...(isCard
      ? {
          // Soft elevated-card shadow instead of the tooltip's flat bubble.
          shadowColor: '#262322',
          shadowOffset: {width: 0, height: 4},
          shadowOpacity: 0.12,
          shadowRadius: 12,
          elevation: 6,
        }
      : null),
  };

  const content = ({position: arrowPosition, childRect, popoverRect}) => (
    <ArrowContainer
      position={arrowPosition}
      childRect={childRect}
      popoverRect={popoverRect}
      // Card variant blends the arrow into the card body instead of a dark
      // speech-bubble point.
      arrowColor={arrowColor || (isCard ? '#FFFFFF' : '#000')}
      arrowSize={8}>
      <View style={[popoverContainerStyle, contentStyle]}>
        {body != null ? (
          body
        ) : (
          <CCText
            color={textcolor || (isCard ? '#262322' : '#fff')}
            fontSize={fontSize || 13}>
            {text}
          </CCText>
        )}
      </View>
    </ArrowContainer>
  );

  return (
    <Popover
      isOpen={isOpen}
      positions={positions ?? [position]}
      boundaryInset={boundaryInset}
      align="center"
      padding={8}
      reposition={true}
      onClickOutside={() => setIsOpen(false)}
      containerStyle={{zIndex: 10000}}
      containerClassName="tooltip-container"
      container={Platform.OS === 'web' ? document.body : undefined}
      content={content}>
      {typeof children === 'function' ? children?.(toggle) : <></>}
    </Popover>
  );
};

export default Tooltip;
